import { type ComponentType, type ReactNode, Suspense, lazy, useContext } from 'react';
import { Navigate, Outlet, RouterProvider, createBrowserRouter, isRouteErrorResponse, useLocation, useParams, useRouteError } from 'react-router-dom';
import { Toaster } from '@components/Toast';
import { ControlPanel } from '@components/ControlPanel';
import { FinancialFarmContent, FinancialFarmNavigation } from '@components/navigation/FinancialFarmNavigation';
import { HelpRuntime } from '@pages/help/runtime/HelpRuntime';
import { AuthContext, AuthProvider } from '@context/AuthContext';
import { FarmProvider } from '@context/FarmContext';
import { UserProvider } from '@context/UserContext';
import ConfirInfosError from '@errors/ConfirmInfoError';
import { appRoutes } from './appRoutes';
import { RouteAccessGuard } from './RouteAccessGuard';

type NamedComponentModule = Record<string, ComponentType<any>>;

function lazyNamed<T extends NamedComponentModule>(factory: () => Promise<T>, exportName: keyof T) {
  return lazy(async () => {
    const module = await factory();
    return { default: module[exportName] as ComponentType<any> };
  });
}

const Login = lazy(async () => await import('../pages/login'));
const ForgotPassword = lazy(async () => await import('@pages/login/forgotPassword'));
const ResetPassword = lazy(async () => await import('@pages/login/resetPassword'));

const DashboardTabs = lazy(async () => await import('@pages/dashboard/dashboard-tabs'));
const Help = lazy(async () => await import('@pages/help/help'));
const FinancialHome = lazy(async () => await import('@pages/financial/FinancialHome'));
const FinancialAccountsPage = lazy(async () => await import('@pages/financial/accounts/FinancialAccountsPage'));
const FinancialCategoriesPage = lazy(async () => await import('@pages/financial/categories/FinancialCategoriesPage'));
const FinancialCostCentersPage = lazy(async () => await import('@pages/financial/costCenters/FinancialCostCentersPage'));
const FinancialProjectsPage = lazy(async () => await import('@pages/financial/projects/FinancialProjectsPage'));
const ProjectDetailPage = lazy(async () => await import('@pages/financial/projects/ProjectDetailPage'));
const FinancialDocumentsPage = lazy(async () => await import('@pages/financial/documents/FinancialDocumentsPage'));
const DocumentDetailPage = lazy(async () => await import('@pages/financial/documents/DocumentDetailPage'));
const FinancialPurchasesPage = lazy(async () => await import('@pages/financial/purchases/FinancialPurchasesPage'));
const FiscalInboxPage = lazy(async () => await import('@pages/financial/fiscalInbox/FiscalInboxPage'));
const FiscalNotePage = lazy(async () => await import('@pages/financial/fiscalInbox/FiscalNotePage'));
const PurchaseDetailPage = lazy(async () => await import('@pages/financial/purchases/PurchaseDetailPage'));
const PurchaseCatalogPage = lazy(async () => await import('@pages/financial/purchases/catalog/PurchaseCatalogPage'));
const PurchaseCatalogItemPage = lazy(async () => await import('@pages/financial/purchases/catalog/PurchaseCatalogItemPage'));
const FinancialCashPage = lazy(async () => await import('@pages/financial/cash/FinancialCashPage'));
const FinancialOverviewPage = lazy(async () => await import('@pages/financial/overview/FinancialOverviewPage'));
const FinancialStockPage = lazy(async () => await import('@pages/financial/stock/FinancialStockPage'));
const FinancialCostingPage = lazy(async () => await import('@pages/financial/costing/FinancialCostingPage'));
const FinancialResultsPage = lazy(async () => await import('@pages/financial/results/FinancialResultsPage'));
const ListReports = lazyNamed(async () => await import('@pages/report/list'), 'ListReports');

const ListFarms = lazyNamed(async () => await import('@pages/local/farms/list'), 'ListFarms');
const ViewFarm = lazyNamed(async () => await import('@pages/local/farms/view'), 'ViewFarm');
const EditFarm = lazyNamed(async () => await import('@pages/local/farms/edit'), 'EditFarm');
const ListAreas = lazyNamed(async () => await import('@pages/local/areas/list'), 'ListAreas');
const ViewArea = lazyNamed(async () => await import('@pages/local/areas/view'), 'ViewArea');
const EditArea = lazyNamed(async () => await import('@pages/local/areas/edit'), 'EditArea');

const ListItems = lazyNamed(async () => await import('@pages/data/items/list'), 'ListItems');
const ViewItem = lazyNamed(async () => await import('@pages/data/items/view'), 'ViewItem');
const EditItem = lazyNamed(async () => await import('@pages/data/items/edit'), 'EditItem');
const ListDiets = lazyNamed(async () => await import('@pages/data/diets/list'), 'ListDiets');
const ViewDiet = lazyNamed(async () => await import('@pages/data/diets/view'), 'ViewDiet');
const EditDiet = lazyNamed(async () => await import('@pages/data/diets/edit'), 'EditDiet');
const ListPrograms = lazyNamed(async () => await import('@pages/data/programs/list'), 'ListPrograms');
const ViewProgram = lazyNamed(async () => await import('@pages/data/programs/view'), 'ViewProgram');
const EditProgram = lazyNamed(async () => await import('@pages/data/programs/edit'), 'EditProgram');
const ListGainCurves = lazyNamed(async () => await import('@pages/data/gainCurve/list'), 'ListGainCurves');
const ViewGainCurve = lazyNamed(async () => await import('@pages/data/gainCurve/view'), 'ViewGainCurve');
const EditGainCurve = lazyNamed(async () => await import('@pages/data/gainCurve/edit'), 'EditGainCurve');

const ListLots = lazyNamed(async () => await import('@pages/management/lots/list'), 'ListLots');
const ViewLot = lazyNamed(async () => await import('@pages/management/lots/view'), 'ViewLot');
const EditLot = lazyNamed(async () => await import('@pages/management/lots/edit'), 'EditLot');
const ListAnimals = lazyNamed(async () => await import('@pages/management/animals/list'), 'ListAnimals');
const MovementHistoryPage = lazyNamed(async () => await import('@pages/management/movements'), 'MovementHistoryPage');
const HandlingPage = lazyNamed(async () => await import('@pages/management/handling'), 'HandlingPage');
const ContractsList = lazy(async () => await import('@pages/management/contracts/list'));
const ContractDetail = lazyNamed(async () => await import('@pages/management/contracts/view'), 'ContractDetail');
const PasturePage = lazyNamed(async () => await import('@pages/management/pasture'), 'PasturePage');
const ListNutritional = lazyNamed(async () => await import('@pages/management/nutritional/list'), 'ListNutritional');
const EditNutritional = lazyNamed(async () => await import('@pages/management/nutritional/edit'), 'EditNutritional');
const ScoreNutritional = lazyNamed(async () => await import('@pages/management/nutritional/score'), 'ScoreNutritional');
const CochoListPage = lazyNamed(async () => await import('@pages/management/nutritional/cocho/list'), 'CochoListPage');
const CochoAreaDetailPage = lazyNamed(async () => await import('@pages/management/nutritional/cocho/area'), 'CochoAreaDetailPage');
const ListProduction = lazyNamed(async () => await import('@pages/management/production/list'), 'ListProduction');
const NewProduction = lazyNamed(async () => await import('@pages/management/production/new'), 'NewProduction');
const ViewProduction = lazyNamed(async () => await import('@pages/management/production/view'), 'ViewProduction');
const ListAssessments = lazyNamed(async () => await import('@pages/management/assessment/list'), 'ListAssessments');
const EditAssessment = lazyNamed(async () => await import('@pages/management/assessment/edit'), 'EditAssessment');
const ListTransfers = lazyNamed(async () => await import('@pages/management/transfers/list'), 'ListTransfers');
const TransferReceptionPage = lazyNamed(async () => await import('@pages/management/transfers/reception'), 'TransferReceptionPage');
const OperacaoPage = lazyNamed(async () => await import('@pages/management/operacao'), 'OperacaoPage');
const AutomacaoPage = lazyNamed(async () => await import('@pages/management/automation'), 'AutomacaoPage');
const PerfisTecnicosPage = lazyNamed(async () => await import('@pages/management/automation/perfis'), 'PerfisTecnicosPage');
const SanitaryPage = lazyNamed(async () => await import('@pages/management/sanitary'), 'SanitaryPage');
const SanitaryProductDetailPage = lazyNamed(async () => await import('@pages/management/sanitary/product'), 'SanitaryProductDetailPage');
const SanitaryProductEditPage = lazyNamed(async () => await import('@pages/management/sanitary/product/edit'), 'SanitaryProductEditPage');
const ListFinances = lazyNamed(async () => await import('@pages/finances/sale/list'), 'ListFinances');
const FinanceDetailPage = lazyNamed(async () => await import('@pages/finances/detail'), 'FinanceDetailPage');
const ViewSale = lazyNamed(async () => await import('@pages/finances/sale/info'), 'ViewSale');

const ListSupplier = lazyNamed(async () => await import('@pages/register/supplier/list'), 'ListSupplier');
const ViewSupplier = lazyNamed(async () => await import('@pages/register/supplier/view'), 'ViewSupplier');
const EditSupplier = lazyNamed(async () => await import('@pages/register/supplier/edit'), 'EditSupplier');
const ListBuyer = lazyNamed(async () => await import('@pages/register/buyer/list'), 'ListBuyer');
const ViewBuyer = lazyNamed(async () => await import('@pages/register/buyer/view'), 'ViewBuyer');
const EditBuyer = lazyNamed(async () => await import('@pages/register/buyer/edit'), 'EditBuyer');
const ListUsers = lazyNamed(async () => await import('@pages/config/users/list'), 'ListUsers');
const ViewUser = lazyNamed(async () => await import('@pages/config/users/view'), 'ViewUser');
const EditUser = lazyNamed(async () => await import('@pages/config/users/edit'), 'EditUser');
const LicensesPage = lazyNamed(async () => await import('@pages/config/licenses'), 'LicensesPage');

function FullScreenFallback() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <p>Carregando...</p>
    </div>
  );
}

function RouteSuspense({ children }: { children: ReactNode }) {
  return <Suspense fallback={<FullScreenFallback />}>{children}</Suspense>;
}

function RouteErrorBoundary() {
  const error = useRouteError();

  let title = 'Não foi possível carregar esta página.';
  let description = 'Ocorreu um erro inesperado ao montar a interface. Recarregue para tentar novamente.';

  if (isRouteErrorResponse(error)) {
    title = `Erro ${error.status}`;
    description = typeof error.data === 'string' ? error.data : error.statusText || description;
  } else if (error instanceof Error && error.message) {
    description = error.message;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: '2rem',
        background: '#eef4f0',
      }}
    >
      <div
        style={{
          width: 'min(520px, 100%)',
          background: '#fff',
          borderRadius: '20px',
          padding: '2.4rem',
          boxShadow: '0 20px 48px rgba(15, 72, 53, 0.12)',
          border: '1px solid rgba(15, 80, 58, 0.12)',
        }}
      >
        <h1 style={{ margin: 0, color: '#0f4d35', fontSize: '2.4rem' }}>{title}</h1>
        <p style={{ margin: '1.2rem 0 1.8rem', color: '#4c6658', lineHeight: 1.6 }}>{description}</p>
        <button
          type="button"
          onClick={() => {
            window.location.reload();
          }}
          style={{
            border: 0,
            borderRadius: '12px',
            padding: '1rem 1.4rem',
            background: '#196f4e',
            color: '#fff',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Recarregar página
        </button>
      </div>
    </div>
  );
}

function RootRedirect() {
  const { isAuthenticated, isLoading } = useContext(AuthContext);

  if (isLoading) {
    return <FullScreenFallback />;
  }

  return <Navigate to={isAuthenticated ? appRoutes.dashboard : appRoutes.auth.login} replace />;
}

/** O Estoque saiu da Gestão: links antigos (`/estoque?mode=sanitario`) abrem o do Financeiro. */
function LegacyStockRedirect() {
  const { search } = useLocation();
  return <Navigate to={`${appRoutes.financeiro.estoque}${search}`} replace />;
}

/** Links antigos de Compradores/Fornecedores (lista, ver e editar) no Financeiro. */
function LegacyPartnerRedirect({ to }: { to: string }) {
  const { '*': rest } = useParams();
  const { search } = useLocation();
  return <Navigate to={`${to}${rest ? `/${rest}` : ''}${search}`} replace />;
}

function ProtectedLayout() {
  const { isAuthenticated, isLoading } = useContext(AuthContext);

  if (isLoading) {
    return <FullScreenFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to={appRoutes.auth.login} replace />;
  }

  return (
    <UserProvider>
      <FarmProvider>
        <FinancialFarmNavigation>
          <ControlPanel>
            <Toaster />
            <HelpRuntime />
            <FinancialFarmContent><Outlet /></FinancialFarmContent>
          </ControlPanel>
        </FinancialFarmNavigation>
      </FarmProvider>
    </UserProvider>
  );
}

function GuardedPage({
  children,
  navKey,
  farms,
  farm,
  scores,
}: {
  children: ReactNode;
  /** Item de navegação cuja regra de permissão/nível/licença a rota reusa. */
  navKey?: string;
  farms?: boolean;
  farm?: boolean;
  scores?: boolean;
}) {
  const content = (
    <ConfirInfosError farms={farms} farm={farm} scores={scores}>
      {children}
    </ConfirInfosError>
  );

  return <RouteSuspense>{navKey ? <RouteAccessGuard navKey={navKey}>{content}</RouteAccessGuard> : content}</RouteSuspense>;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <Outlet />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <RootRedirect />,
      },
      {
        path: appRoutes.auth.login.slice(1),
        element: (
          <RouteSuspense>
            <Login />
          </RouteSuspense>
        ),
      },
      {
        path: appRoutes.auth.forgotPassword.slice(1),
        element: (
          <RouteSuspense>
            <ForgotPassword />
          </RouteSuspense>
        ),
      },
      {
        path: 'forgot-password',
        element: <Navigate to={appRoutes.auth.forgotPassword} replace />,
      },
      {
        path: appRoutes.auth.resetPassword.slice(1),
        element: (
          <RouteSuspense>
            <ResetPassword />
          </RouteSuspense>
        ),
      },
      {
        element: <ProtectedLayout />,
        children: [
          {
            path: appRoutes.dashboard.slice(1),
            element: (
              <GuardedPage farms={true} farm={true}>
                <DashboardTabs />
              </GuardedPage>
            ),
          },
          {
            path: `${appRoutes.ajuda.slice(1)}/:pieceId?`,
            element: (
              <RouteSuspense>
                <Help />
              </RouteSuspense>
            ),
          },
          {
            path: appRoutes.fazendas.list.slice(1),
            element: (
              <GuardedPage navKey="fazendas">
                <ListFarms />
              </GuardedPage>
            ),
          },
          {
            path: 'fazendas/:id',
            element: (
              <GuardedPage navKey="fazendas">
                <ViewFarm />
              </GuardedPage>
            ),
          },
          {
            path: 'fazendas/:id/editar',
            element: (
              <GuardedPage navKey="fazendas">
                <EditFarm />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.areas.list.slice(1),
            element: (
              <GuardedPage navKey="areas" farms={true} farm={true}>
                <ListAreas />
              </GuardedPage>
            ),
          },
          {
            path: 'areas/:id',
            element: (
              <GuardedPage navKey="areas" farms={true} farm={true}>
                <ViewArea />
              </GuardedPage>
            ),
          },
          {
            path: 'areas/:id/editar',
            element: (
              <GuardedPage navKey="areas" farms={true} farm={true}>
                <EditArea />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.ingredientes.list.slice(1),
            element: (
              <GuardedPage navKey="ingredientes" farms={true} farm={true}>
                <ListItems />
              </GuardedPage>
            ),
          },
          {
            path: 'ingredientes/:id',
            element: (
              <GuardedPage navKey="ingredientes" farms={true} farm={true}>
                <ViewItem />
              </GuardedPage>
            ),
          },
          {
            path: 'ingredientes/:id/editar',
            element: (
              <GuardedPage navKey="ingredientes" farms={true} farm={true}>
                <EditItem />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.formulas.list.slice(1),
            element: (
              <GuardedPage navKey="formulas" farms={true} farm={true}>
                <ListDiets />
              </GuardedPage>
            ),
          },
          {
            path: 'formulas/:id',
            element: (
              <GuardedPage navKey="formulas" farms={true} farm={true}>
                <ViewDiet />
              </GuardedPage>
            ),
          },
          {
            path: 'formulas/:id/editar',
            element: (
              <GuardedPage navKey="formulas" farms={true} farm={true}>
                <EditDiet />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.programas.list.slice(1),
            element: (
              <GuardedPage navKey="programas" farms={true} farm={true}>
                <ListPrograms />
              </GuardedPage>
            ),
          },
          {
            path: 'programas/:id',
            element: (
              <GuardedPage navKey="programas" farms={true} farm={true}>
                <ViewProgram />
              </GuardedPage>
            ),
          },
          {
            path: 'programas/:id/editar',
            element: (
              <GuardedPage navKey="programas" farms={true} farm={true}>
                <EditProgram />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.curvasDeGanho.list.slice(1),
            element: (
              <GuardedPage navKey="curvas-de-ganho" farms={true} farm={true}>
                <ListGainCurves />
              </GuardedPage>
            ),
          },
          {
            path: 'curvas-de-ganho/:id',
            element: (
              <GuardedPage navKey="curvas-de-ganho" farms={true} farm={true}>
                <ViewGainCurve />
              </GuardedPage>
            ),
          },
          {
            path: 'curvas-de-ganho/:id/editar',
            element: (
              <GuardedPage navKey="curvas-de-ganho" farms={true} farm={true}>
                <EditGainCurve />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.lotes.list.slice(1),
            element: (
              <GuardedPage navKey="lotes" farms={true} farm={true}>
                <ListLots />
              </GuardedPage>
            ),
          },
          {
            path: 'lotes/:id',
            element: (
              <GuardedPage navKey="lotes" farms={true} farm={true}>
                <ViewLot />
              </GuardedPage>
            ),
          },
          {
            path: 'lotes/:id/editar',
            element: (
              <GuardedPage navKey="lotes" farms={true} farm={true}>
                <EditLot />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.animais.list.slice(1),
            element: (
              <GuardedPage navKey="animais" farms={true} farm={true}>
                <ListAnimals />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.movimentacoes.list.slice(1),
            element: (
              <GuardedPage navKey="movimentacoes" farms={true} farm={true}>
                <MovementHistoryPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.manejo.list.slice(1),
            element: (
              <GuardedPage navKey="manejo" farms={true} farm={true}>
                <HandlingPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.contratos.list.slice(1),
            element: (
              <GuardedPage navKey="contratos" farms={true} farm={true}>
                <ContractsList />
              </GuardedPage>
            ),
          },
          {
            path: 'contratos/:id',
            element: (
              <GuardedPage navKey="contratos" farms={true} farm={true}>
                <ContractDetail />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.pasto.slice(1),
            element: (
              <GuardedPage navKey="pasto" farms={true} farm={true}>
                <PasturePage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.nutricao.list.slice(1),
            element: (
              <GuardedPage navKey="nutricao" farms={true} farm={true} scores={true}>
                <ListNutritional />
              </GuardedPage>
            ),
          },
          {
            path: 'nutricao/:date',
            element: (
              <GuardedPage navKey="nutricao" farms={true} farm={true} scores={true}>
                <EditNutritional />
              </GuardedPage>
            ),
          },
          {
            path: 'nutricao/:date/ajuste-diario',
            element: (
              <GuardedPage navKey="nutricao" farms={true} farm={true} scores={true}>
                <ScoreNutritional />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.cocho.list.slice(1),
            element: (
              <GuardedPage navKey="cocho" farms={true} farm={true} scores={true}>
                <CochoListPage />
              </GuardedPage>
            ),
          },
          {
            path: 'cocho/areas/:areaId',
            element: (
              <GuardedPage navKey="cocho" farms={true} farm={true} scores={true}>
                <CochoAreaDetailPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.producao.list.slice(1),
            element: (
              <GuardedPage navKey="producao" farms={true} farm={true}>
                <ListProduction />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.producao.nova.slice(1),
            element: (
              <GuardedPage navKey="producao" farms={true} farm={true}>
                <NewProduction />
              </GuardedPage>
            ),
          },
          {
            path: 'producao/:id',
            element: (
              <GuardedPage navKey="producao" farms={true} farm={true}>
                <ViewProduction />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.estoque.slice(1),
            element: <LegacyStockRedirect />,
          },
          // Financeiro gerencial: escopo de fazenda próprio (não exige a fazenda global).
          {
            path: appRoutes.financeiro.root.slice(1),
            element: (
              <GuardedPage farms={true}>
                <FinancialHome />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.resumo.slice(1),
            element: (
              <GuardedPage navKey="financeiro-resumo" farms={true}>
                <FinancialOverviewPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.caixa.slice(1),
            element: (
              <GuardedPage navKey="financeiro-caixa" farms={true}>
                <FinancialCashPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.aPagar.slice(1),
            element: (
              <GuardedPage navKey="financeiro-a-pagar" farms={true}>
                <FinancialDocumentsPage key="payable" direction="payable" />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.aReceber.slice(1),
            element: (
              <GuardedPage navKey="financeiro-a-receber" farms={true}>
                <FinancialDocumentsPage key="receivable" direction="receivable" />
              </GuardedPage>
            ),
          },
          {
            path: `${appRoutes.financeiro.aPagar.slice(1)}/:documentId`,
            element: (
              <GuardedPage navKey="financeiro-a-pagar" farms={true}>
                <DocumentDetailPage key="payable" direction="payable" />
              </GuardedPage>
            ),
          },
          {
            path: `${appRoutes.financeiro.aReceber.slice(1)}/:documentId`,
            element: (
              <GuardedPage navKey="financeiro-a-receber" farms={true}>
                <DocumentDetailPage key="receivable" direction="receivable" />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.compras.slice(1),
            element: (
              <GuardedPage navKey="financeiro-compras" farms={true}>
                <FinancialPurchasesPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.notasRecebidas.slice(1),
            element: <GuardedPage navKey="financeiro-notas-recebidas" farms={true}><FiscalInboxPage /></GuardedPage>,
          },
          {
            path: `${appRoutes.financeiro.notasRecebidas.slice(1)}/:noteId`,
            element: <GuardedPage navKey="financeiro-notas-recebidas" farms={true}><FiscalNotePage /></GuardedPage>,
          },
          {
            path: appRoutes.financeiro.itensCompra.slice(1),
            element: (
              <GuardedPage navKey="financeiro-compras" farms={true}>
                <PurchaseCatalogPage />
              </GuardedPage>
            ),
          },
          {
            path: `${appRoutes.financeiro.itensCompra.slice(1)}/:itemId`,
            element: (
              <GuardedPage navKey="financeiro-compras" farms={true}>
                <PurchaseCatalogItemPage />
              </GuardedPage>
            ),
          },
          {
            path: `${appRoutes.financeiro.compras.slice(1)}/:purchaseId`,
            element: (
              <GuardedPage navKey="financeiro-compras" farms={true}>
                <PurchaseDetailPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.estoque.slice(1),
            element: (
              <GuardedPage navKey="financeiro-estoque" farms={true}>
                <FinancialStockPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.custeio.slice(1),
            element: (
              <GuardedPage navKey="financeiro-custeio" farms={true}>
                <FinancialCostingPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.dre.slice(1),
            element: (
              <GuardedPage navKey="financeiro-dre" farms={true}>
                <FinancialResultsPage key="dre" view="dre" />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.custosDoPeriodo.slice(1),
            element: (
              <GuardedPage navKey="financeiro-custos-do-periodo" farms={true}>
                <FinancialResultsPage key="period" view="period" />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.fornecedores.list.slice(1),
            element: (
              <GuardedPage navKey="fornecedores" farms={true}>
                <ListSupplier />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.fornecedores.view(':id').slice(1),
            element: (
              <GuardedPage navKey="fornecedores" farms={true}>
                <ViewSupplier />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.fornecedores.edit(':id').slice(1),
            element: (
              <GuardedPage navKey="fornecedores" farms={true}>
                <EditSupplier />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.compradores.list.slice(1),
            element: (
              <GuardedPage navKey="compradores" farms={true}>
                <ListBuyer />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.compradores.view(':id').slice(1),
            element: (
              <GuardedPage navKey="compradores" farms={true}>
                <ViewBuyer />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.compradores.edit(':id').slice(1),
            element: (
              <GuardedPage navKey="compradores" farms={true}>
                <EditBuyer />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.contasFinanceiras.slice(1),
            element: (
              <GuardedPage navKey="financeiro-contas-financeiras" farms={true}>
                <FinancialAccountsPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.categorias.slice(1),
            element: (
              <GuardedPage navKey="financeiro-categorias" farms={true}>
                <FinancialCategoriesPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.centros.slice(1),
            element: (
              <GuardedPage navKey="financeiro-centros" farms={true}>
                <FinancialCostCentersPage key="centers" />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.subcentros.slice(1),
            element: (
              <GuardedPage navKey="financeiro-centros" farms={true}>
                <FinancialCostCentersPage key="subcenters" view="subcenters" />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financeiro.projetos.slice(1),
            element: (
              <GuardedPage navKey="financeiro-projetos" farms={true}>
                <FinancialProjectsPage />
              </GuardedPage>
            ),
          },
          {
            path: `${appRoutes.financeiro.projetos.slice(1)}/:projectId`,
            element: (
              <GuardedPage navKey="financeiro-projetos" farms={true}>
                <ProjectDetailPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financasDetalhe.slice(1),
            element: (
              <GuardedPage navKey="financas-detalhe" farms={true} farm={true}>
                <FinanceDetailPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.financas.list.slice(1),
            element: (
              <GuardedPage navKey="financas" farms={true} farm={true}>
                <ListFinances />
              </GuardedPage>
            ),
          },
          {
            path: 'financas/:id',
            element: (
              <GuardedPage navKey="financas" farms={true} farm={true}>
                <ViewSale />
              </GuardedPage>
            ),
          },
          // Compradores e Fornecedores saíram da Gestão: links antigos abrem o Financeiro.
          { path: 'fornecedores/*', element: <LegacyPartnerRedirect to={appRoutes.fornecedores.list} /> },
          { path: 'compradores/*', element: <LegacyPartnerRedirect to={appRoutes.compradores.list} /> },
          {
            path: appRoutes.relatorios.slice(1),
            element: (
              <GuardedPage navKey="relatorios">
                <ListReports />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.avaliacoes.list.slice(1),
            element: (
              <GuardedPage navKey="avaliacoes" farms={true} farm={true}>
                <ListAssessments />
              </GuardedPage>
            ),
          },
          {
            path: 'avaliacoes/:id/editar',
            element: (
              <GuardedPage navKey="avaliacoes" farms={true} farm={true}>
                <EditAssessment />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.transferencias.list.slice(1),
            element: (
              <GuardedPage navKey="transferencias" farms={true} farm={true}>
                <ListTransfers />
              </GuardedPage>
            ),
          },
          {
            path: 'transferencias/:id/receber',
            element: (
              <GuardedPage navKey="transferencias" farms={true} farm={true}>
                <TransferReceptionPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.operacao.list.slice(1),
            element: (
              <GuardedPage navKey="operacao">
                <OperacaoPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.automacao.list.slice(1),
            element: (
              <GuardedPage navKey="automacao" farms={true} farm={true}>
                <AutomacaoPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.sanitario.list.slice(1),
            element: (
              <GuardedPage navKey="sanitario" farms={true} farm={true}>
                <SanitaryPage />
              </GuardedPage>
            ),
          },
          {
            path: 'sanitario/produtos/:id',
            element: (
              <GuardedPage navKey="sanitario" farms={true} farm={true}>
                <SanitaryProductDetailPage />
              </GuardedPage>
            ),
          },
          {
            path: 'sanitario/produtos/:id/editar',
            element: (
              <GuardedPage navKey="sanitario" farms={true} farm={true}>
                <SanitaryProductEditPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.usuarios.list.slice(1),
            element: (
              <GuardedPage navKey="usuarios">
                <ListUsers />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.automacao.perfisTecnicos.slice(1),
            element: (
              <GuardedPage navKey="automacaoPerfis">
                <PerfisTecnicosPage />
              </GuardedPage>
            ),
          },
          {
            path: appRoutes.licencas.slice(1),
            element: (
              <GuardedPage navKey="licencas">
                <LicensesPage />
              </GuardedPage>
            ),
          },
          {
            path: 'usuarios/:id',
            element: (
              <GuardedPage navKey="usuarios">
                <ViewUser />
              </GuardedPage>
            ),
          },
          {
            path: 'usuarios/:id/editar',
            element: (
              <GuardedPage navKey="usuarios">
                <EditUser />
              </GuardedPage>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

export default function MainRoutes() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
